<script>
    import { mapState } from "vuex";
    import { CITIES_LIST } from "@/settings/constants";
    import ProductHeader from "@/components/product/ProductHeader";
    import ProductAvailabilityState from "@/components/product/ProductAvailabilityState";
    import ProductControls from "@/components/product/ProductControls";
    import ProductDelivery from "@/components/product/ProductDelivery";
    import ProductStoreList from "@/components/product/ProductStoreList";
    import ProductFeaturesList from "@/components/product/ProductFeaturesList";
    import ProductPartnersList from "@/components/product/ProductPartnersList";
    import ProductPrice from "@nanoleaf/components/ProductPrice";

    export default {
        name: "ProductAside",
        components: {
            ProductPartnersList,
            ProductHeader,
            ProductFeaturesList,
            ProductStoreList,
            ProductDelivery,
            ProductControls,
            ProductPrice,
            ProductAvailabilityState,
        },
        props: {
            product: Object,
            listForHeader: Array,
            inCartList: Array,
        },
        data() {
            let currentOffers = this.product.offers.filter((offer) => offer.available);

            if (currentOffers.length === 0) {
                currentOffers = this.product.offers.filter((offer) => !offer.available);
            }

            return {
                currentOffer: currentOffers[0],
            };
        },
        computed: {
            ...mapState("Root", ["city"]),
            isAvailable() {
                return this.currentOffer.quantity > 0 && this.currentOffer.available;
            },
            productInCart() {
                return this.inCartList.includes(this.currentOffer.id.toString());
            },
            showFeaturesList() {
                return this.product.features.length > 0;
            },
            showStoreList() {
                return CITIES_LIST.includes(this.city);
            },
        },
        methods: {
            anchorScrollHandler() {
                this.$emit("on-scroll-to-anchor");
            },
        },
    };
</script>

<template>
    <div class="product-aside">
        <product-header :product="product" :offer="currentOffer" :list-anchor="listForHeader">
            <template #price>
                <product-price
                    :price="currentOffer.discountPrice"
                    :old-price="currentOffer.price"
                    :discount-count="currentOffer.discountPercent"
                    is-small-size
                ></product-price>
            </template>
            <template #button>
                <product-controls
                    :offer-id="currentOffer.id"
                    :product="product"
                    :price="currentOffer.discountPrice"
                    :is-pre-order="!isAvailable"
                    :in-cart="productInCart"
                    variant-one-click="v3"
                    variant-pre-order="v2"
                    :is-for-header="true"
                >
                </product-controls>
            </template>
        </product-header>

        <div class="product-aside__mobile-wrapper">
            <h1 class="product-aside__title" itemprop="name">
                {{ product.name }}
            </h1>

            <div class="product-aside__availability">
                <product-availability-state
                    :is-available="isAvailable"
                    is-common
                ></product-availability-state>
            </div>

            <div class="product-aside__price">
                <product-price
                    :price="currentOffer.discountPrice"
                    :old-price="currentOffer.price"
                    :discount-count="currentOffer.discountPercent"
                ></product-price>
            </div>

            <div class="product-aside__controls js-product-aside-controls">
                <product-controls
                    :offer-id="currentOffer.id"
                    :product="product"
                    :price="currentOffer.discountPrice"
                    :is-pre-order="!isAvailable"
                    :in-cart="productInCart"
                    variant-one-click="v3"
                    variant-pre-order="v2"
                >
                </product-controls>
            </div>
        </div>

        <div v-if="!isAvailable" class="product-aside__partners">
            <product-partners-list
                :product-id="product.id"
                :offer-id="currentOffer.id"
            ></product-partners-list>
        </div>

        <div v-if="isAvailable" class="product-aside__delivery">
            <product-delivery :city="city" :offer-id="currentOffer.id"></product-delivery>
        </div>

        <div v-if="showStoreList && isAvailable" class="product-aside__stores">
            <product-store-list :city="city" :offer-id="currentOffer.id"></product-store-list>
        </div>

        <div v-if="showFeaturesList" class="product-aside__features">
            <product-features-list
                :features="product.features"
                @on-scroll-to-anchor="anchorScrollHandler"
            ></product-features-list>
        </div>
    </div>
</template>

<style lang="scss">
    .product-aside {
        padding: 24px;
        background: #ffffff;
        border-radius: var(--border-lg);
        border: 1px solid var(--primary-border);
    }

    .product-aside__mobile-wrapper {
        margin-bottom: 16px;
        border-bottom: 1px solid var(--primary-border);
    }

    .product-aside__title {
        margin-bottom: 8px;
        font-size: 16px;
        font-weight: bold;
        line-height: 20px;
        @include bp($bp-desktop-sm) {
            margin-bottom: 12px;
            font-size: 22px;
            line-height: 30px;
        }
    }

    .product-aside__availability {
        margin-bottom: 16px;
        @include bp($bp-desktop-sm) {
            margin-bottom: 24px;
        }
    }

    .product-aside__price {
        margin: 16px 0;
        @include bp($bp-desktop-sm) {
            margin: 24px 0;
        }
    }

    .product-aside__controls {
        min-height: 80px;
        padding-bottom: 24px;
        @include bp($bp-mobile-sm) {
            min-height: 1px;
        }
        .product-controls__btn {
            width: calc(50% - 4px);
            margin-bottom: 16px;
            @include bp_max($bp_desktop-sm_max) {
                width: calc(50% - 12px - 50px / 2);
                margin-bottom: 0;
            }
            @include bp_max($bp_mobile-sm_max) {
                width: 100%;
            }
        }

        .product-controls__btn--buy {
            @include bp_max($bp_mobile-sm_max) {
                margin-bottom: 12px;
            }
        }

        .product-controls__btn--one-click {
            @include bp_max($bp_mobile-sm_max) {
                width: 100%;
            }
        }

        .product-controls__btn--preorder {
            width: 100%;
        }
    }

    .product-aside__stores {
        margin: 16px 0;
        @include bp($bp-desktop-sm) {
            margin: 24px 0;
        }
        span {
            font-weight: 500;
        }

        .product-store-list {
            margin: 0 -12px;
            padding: 12px;
            background-color: var(--service-background-color);
        }

        .product-store-list__title {
            font-weight: 700;
        }
    }

    .product-aside__features {
        margin-top: 16px;
        @include bp($bp-desktop-sm) {
            margin-top: 24px;
        }
        .product-features-list__title {
            font-size: 16px;
        }
    }
</style>
